import React, { useEffect } from 'react';

import classNames from 'classnames';

import { DeleteConfirm } from './DeleteConfirm';
import cls from './FilePreviewer.module.css';
import { InvalidFormatPreview } from './InvalidFormatPreview';
import { PreviewerArrow } from './PreviewerArrow';
import { PreviewerHeader } from './PreviewerHeader';
import { PreviewerImg } from './PreviewerImg';
import { PreviewFrame } from './PreviewFrame';
import { IModalAttachment } from '../../../entities/attachment/model/IModalAttachment.interface';
import { IAttachmentResponse } from '../../../entities/task/model/types/tasks';
import { useFilePreviewer } from '../lib/hooks/useFilePreviewer';
import { usePreviewDirections } from '../lib/hooks/usePreviewDirections';

interface IPreviewModal {
    onHide: () => void;
    onDeleteFile: (fileId: string) => void;
    isOpen: boolean;
    filesToPreview: (IModalAttachment | IAttachmentResponse)[];
    fileIdToPreview: string;
    canDelete?: boolean;
}

export const FilePreviewer = ({
    isOpen = false,
    onHide,
    onDeleteFile,
    filesToPreview,
    fileIdToPreview,
    canDelete = true,
}: IPreviewModal) => {
    const { currentFile, goPrev, goNext, isLast, isFirst } = usePreviewDirections({
        attachments: filesToPreview,
        fileIdToPreview,
    });
    const {
        previewUrl,
        ref,
        decodeNameFile,
        name,
        handleDownload,
        handleDelete,
        isImage,
        isPdf,
        isFromClient,
        iFrameSrc,
        deleteConfirmModal,
        setDeleteConfirmModal,
    } = useFilePreviewer({ onDeleteFile, onHide, filesToPreview, fileIdToPreview, currentFile });
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onHide();
            } else if (e.key === 'ArrowLeft' && !isFirst) {
                goPrev();
            } else if (e.key === 'ArrowRight' && !isLast) {
                goNext();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isFirst, isLast, goPrev, goNext, onHide]);

    return (
        <>
            {isOpen && (
                <div className={classNames(cls.customModal)}>
                    {deleteConfirmModal && (
                        <DeleteConfirm
                            onDeleteFile={handleDelete}
                            onHideModal={() => setDeleteConfirmModal(false)}
                            fileId={String(currentFile.id)}
                        />
                    )}
                    <div className={classNames(cls.customModalOverlay)} ref={ref} onClick={onHide}>
                        <PreviewerArrow
                            arrowType="prev"
                            goPrevHandler={goPrev}
                            isFirst={isFirst}
                            isLast={isLast}
                        />
                        <PreviewerArrow
                            arrowType="next"
                            goNextHandler={goNext}
                            isFirst={isFirst}
                            isLast={isLast}
                        />
                        <PreviewerHeader
                            decodeNameFile={decodeNameFile}
                            setDeleteConfirmModal={setDeleteConfirmModal}
                            canDelete={canDelete}
                            onHide={onHide}
                            handleDownload={handleDownload}
                        />
                        {iFrameSrc || isPdf ? (
                            <PreviewFrame
                                previewUrl={previewUrl}
                                iFrameSrc={iFrameSrc}
                                name={name}
                            />
                        ) : (
                            <div>
                                {isImage ? (
                                    <PreviewerImg
                                        previewUrl={previewUrl}
                                        currentFile={currentFile}
                                        isFromClient={isFromClient}
                                    />
                                ) : (
                                    <InvalidFormatPreview handleDownload={handleDownload} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
